import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useUpdateAccountMutation,
  useSetEntityAssociationMutation,
  useRemoveEntityAssociationMutation,
  AccountPropertiesInput,
} from '@/lib/graphql/types-and-hooks';
import { isLovablePreview } from '@/lib/utils/environment';
import { toast } from 'sonner';
import { debounce } from 'lodash';
import { useCanMutate, assertCanMutate } from '@/lib/rbac';
import { useWorkspaceId } from '@/lib/hooks/use-workspace-id';

// Account associations are account<->account edges; both ends are accounts. The
// backend needs the entity type to translate a Postgres id into its canonical ddb id.
const ACCOUNT_ENTITY_TYPE = 'ACCOUNT';

/**
 * Hook for updating account (company) properties with backend integration
 * Handles validation, debouncing, and Lovable Preview mode
 */
export const useAccountUpdate = (accountId: string, onSuccess?: () => void) => {
  const { t } = useTranslation();
  const canMutate = useCanMutate();
  const workspaceId = useWorkspaceId();
  const [setEntityAssociation] = useSetEntityAssociationMutation();
  const [removeEntityAssociation] = useRemoveEntityAssociationMutation();
  const [updateAccount, { loading, error }] = useUpdateAccountMutation({
    // No refetchQueries — that would reset the windowed grid to the top. The grid now
    // fetches pages with `no-cache` (useWindowedConnection), so mutation responses no
    // longer propagate into it via the normalized cache. CompaniesPage instead reflects
    // edits through its cellEdits overlay + the CompanyDetailSheet onFieldUpdate callback.
  });

  /**
   * Update account properties in backend
   * Optimistic updates handled by Apollo cache
   */
  const updateAccountProperties = useCallback(
    async (properties: Partial<AccountPropertiesInput>) => {
      if (!assertCanMutate(canMutate)) return { success: false };
      // In Lovable Preview mode, just show success toast
      if (isLovablePreview()) {
        toast.success(t('mutations.accountUpdatedPreview'));
        return { success: true };
      }

      try {
        const result = await updateAccount({
          variables: {
            input: {
              id: accountId,
              properties,
            },
          },
        });

        if (result.data?.updateAccount) {
          // Shared id with useCompanyMutations.updateAccount: a detail-sheet edit can
          // persist through both hooks (sheet updater + grid debounced save), so the
          // second toast replaces the first instead of stacking a duplicate.
          toast.success(t('mutations.companyUpdated'), { id: `company-update-${accountId}` });
          onSuccess?.();
          return { success: true, data: result.data.updateAccount };
        }

        return { success: false };
      } catch (err) {
        console.error('Failed to update account:', err);
        // Extract error message from Apollo error or use default
        const errorMessage = (err as any)?.message || t('mutations.accountUpdateFailed');
        toast.error(errorMessage, { id: `company-update-${accountId}` });
        return { success: false, error: err };
      }
    },
    [accountId, updateAccount, onSuccess, canMutate, t]
  );

  /**
   * Debounced update for text fields (500ms delay)
   * Prevents excessive API calls while user is typing
   */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedUpdate = useCallback(
    debounce((properties: Partial<AccountPropertiesInput>) => {
      updateAccountProperties(properties);
    }, 500),
    [updateAccountProperties]
  );

  /**
   * Update company name
   */
  const updateName = useCallback(
    (name: string) => {
      return debouncedUpdate({ name });
    },
    [debouncedUpdate]
  );

  /**
   * Update domain/website
   */
  const updateDomain = useCallback(
    (domain: string) => {
      return debouncedUpdate({ domain });
    },
    [debouncedUpdate]
  );

  /**
   * Update phone number
   */
  const updatePhone = useCallback(
    (phone: string) => {
      return debouncedUpdate({ phone });
    },
    [debouncedUpdate]
  );

  /**
   * Update location (city, state, country)
   */
  const updateLocation = useCallback(
    (location: { city?: string; state?: string; country?: string }) => {
      return debouncedUpdate(location);
    },
    [debouncedUpdate]
  );

  /**
   * Update LinkedIn URL
   */
  const updateLinkedIn = useCallback(
    (linkedinUrl: string) => {
      return updateAccountProperties({ linkedinUrl });
    },
    [updateAccountProperties]
  );

  /**
   * Update industry
   */
  const updateIndustry = useCallback(
    (industry: string) => {
      return debouncedUpdate({ industry });
    },
    [debouncedUpdate]
  );

  /**
   * Update number of employees (matches CompaniesPage implementation)
   */
  const updateEmployees = useCallback(
    (min: number, max: number | null, label: string) => {
      return debouncedUpdate({
        employeeCountMin: min,
        employeeCountMax: max,
        employeeCount: label,
      });
    },
    [debouncedUpdate]
  );

  /**
   * Update revenue range (matches CompaniesPage implementation)
   */
  const updateRevenue = useCallback(
    (min: number, max: number | null, label: string) => {
      return debouncedUpdate({
        revenueMin: min,
        revenueMax: max,
        revenueRange: label,
      });
    },
    [debouncedUpdate]
  );

  /**
   * Update description
   */
  const updateDescription = useCallback(
    (description: string) => {
      return debouncedUpdate({ description });
    },
    [debouncedUpdate]
  );

  /**
   * Update additional domains (websites beyond primary domain)
   * First domain in array becomes the primary domain
   * Remaining domains go to additionalDomains
   *
   * Note: This updates both domain and additionalDomains atomically
   * to avoid duplicate domain errors
   */
  const updateAdditionalDomains = useCallback(
    (domains: string[], currentDomain?: string | null, currentAdditionalDomains?: string[] | null) => {
      const [primaryDomain, ...additionalDomains] = domains;

      // Build update object - only include fields that changed
      const update: Partial<AccountPropertiesInput> = {};

      // Check if primary domain changed
      const newPrimaryDomain = primaryDomain || null;
      if (newPrimaryDomain !== currentDomain) {
        update.domain = newPrimaryDomain as any;
      }

      // Check if additional domains changed
      const newAdditionalDomains = additionalDomains.length > 0 ? additionalDomains : null;
      const currentAdditionalDomainsNormalized = currentAdditionalDomains && currentAdditionalDomains.length > 0 ? currentAdditionalDomains : null;

      // Compare arrays
      const additionalDomainsChanged = JSON.stringify(newAdditionalDomains) !== JSON.stringify(currentAdditionalDomainsNormalized);
      if (additionalDomainsChanged) {
        update.additionalDomains = newAdditionalDomains as any;
      }

      // Only send update if something changed
      if (Object.keys(update).length === 0) {
        console.log('[updateAdditionalDomains] No changes detected, skipping update');
        return Promise.resolve({ success: true });
      }

      return debouncedUpdate(update);
    },
    [debouncedUpdate]
  );

  /**
   * Create/refresh a labeled association from this account to another account.
   * `label` carries the relationship type (e.g. "subsidiary"). Persists both
   * directed edges backend-side; the caller should refetch the account to reflect
   * the canonical state (and the reverse relationship). No-op in preview mode.
   */
  const setAssociation = useCallback(
    async (toId: string, label: string) => {
      if (!assertCanMutate(canMutate)) return { success: false };
      if (isLovablePreview()) return { success: true };
      try {
        await setEntityAssociation({
          variables: {
            workspaceId: String(workspaceId),
            input: {
              fromId: accountId,
              toId,
              label,
              fromType: ACCOUNT_ENTITY_TYPE,
              toType: ACCOUNT_ENTITY_TYPE,
            },
          },
        });
        return { success: true };
      } catch (err) {
        console.error('Failed to set association:', err);
        toast.error((err as any)?.message || t('mutations.associationSaveFailed'));
        return { success: false, error: err };
      }
    },
    [accountId, workspaceId, setEntityAssociation, canMutate, t]
  );

  /**
   * Remove a labeled association between this account and another account.
   * No-op in preview mode.
   */
  const removeAssociation = useCallback(
    async (toId: string, label: string) => {
      if (!assertCanMutate(canMutate)) return { success: false };
      if (isLovablePreview()) return { success: true };
      try {
        await removeEntityAssociation({
          variables: {
            workspaceId: String(workspaceId),
            fromId: accountId,
            toId,
            label,
            fromType: ACCOUNT_ENTITY_TYPE,
            toType: ACCOUNT_ENTITY_TYPE,
          },
        });
        return { success: true };
      } catch (err) {
        console.error('Failed to remove association:', err);
        toast.error((err as any)?.message || t('mutations.associationRemoveFailed'));
        return { success: false, error: err };
      }
    },
    [accountId, workspaceId, removeEntityAssociation, canMutate, t]
  );

  return {
    updateName,
    updateDomain,
    updatePhone,
    updateLocation,
    updateLinkedIn,
    updateIndustry,
    updateEmployees,
    updateRevenue,
    updateDescription,
    updateAdditionalDomains,
    setAssociation,
    removeAssociation,
    loading,
    error,
  };
};
